import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, lastValueFrom } from 'rxjs';
import { Log, IShortLogger } from '../logger.service';
import { ConfigService } from '../config.service';
import { TeamEntity } from '../../models/entities/team-entity';
import { UserEntity } from '../../models/entities/user-entity';

/**
 * Service for managing team entities (CRUD operations and user reassignment).
 * Uses ConfigService to get API URL dynamically.
 */
@Injectable({
    providedIn: 'root'
})
export class TeamEntityService {
    private readonly logger: IShortLogger<TeamEntityService> = Log.create(TeamEntityService);
    private baseUrl: string;

    constructor(
        private http: HttpClient,
        private configService: ConfigService
    ) {
        this.baseUrl = this.configService.getApiUrl();
        this.logger.d('TeamEntityService initialized with baseUrl:', this.baseUrl);
    }

    /**
     * Get all teams
     * @returns Observable of team array
     */
    getAllTeams(): Observable<TeamEntity[]> {
        this.logger.d('Getting all teams');
        return this.http.get<TeamEntity[]>(`${this.baseUrl}/v1/teams`);
    }

    /**
     * Get a specific team by ID
     * @param id Team ID
     * @returns Observable of team entity
     */
    getTeamById(id: string): Observable<TeamEntity> {
        this.logger.d('Getting team by ID:', id);
        return this.http.get<TeamEntity>(`${this.baseUrl}/v1/teams/${id}`);
    }

    /**
     * Get all users belonging to a specific team
     * @param teamId Team ID
     * @returns Observable of user array
     */
    getUsersByTeam(teamId: string): Observable<UserEntity[]> {
        this.logger.d('Getting users for team:', teamId);
        return this.http.get<UserEntity[]>(`${this.baseUrl}/v1/teams/${teamId}/users`);
    }

    /**
     * Create a new team
     * @param team Team entity to create
     * @returns Promise of created team entity
     */
    async createTeam(team: TeamEntity): Promise<TeamEntity> {
        const headers = { 'content-type': 'application/json' };
        const body = JSON.stringify({
            name: team.name,
            isDeletable: team.isDeletable ?? false
        });

        this.logger.i('Creating team:', team.name, 'isDeletable:', team.isDeletable);
        const created = await lastValueFrom(
            this.http.post<TeamEntity>(`${this.baseUrl}/v1/teams`, body, { headers })
        );
        return created;
    }

    /**
     * Update an existing team
     * @param team Team entity with updated data
     * @returns Observable of updated team
     */
    updateTeam(team: TeamEntity): Observable<TeamEntity> {
        const headers = { 'content-type': 'application/json' };
        const body = JSON.stringify({
            id: team.id,
            name: team.name,
            isDeletable: team.isDeletable
        });

        this.logger.i('Updating team:', team.id, 'name:', team.name);
        return this.http.put<TeamEntity>(`${this.baseUrl}/v1/teams/${team.id}`, body, { headers });
    }

    /**
     * Reassign users from one team to another
     * @param sourceTeamId Source team ID
     * @param userIds Array of user IDs to reassign
     * @param targetTeamId Target team ID
     * @returns Observable of reassignment result
     */
    reassignUsers(sourceTeamId: string, userIds: string[], targetTeamId: string): Observable<any> {
        const headers = { 'content-type': 'application/json' };
        const body = JSON.stringify({
            userIds: userIds,
            targetTeamId: targetTeamId
        });

        this.logger.i('Reassigning', userIds.length, 'users from team', sourceTeamId, 'to team', targetTeamId);
        return this.http.post(`${this.baseUrl}/v1/teams/${sourceTeamId}/reassign-users`, body, { headers });
    }

    /**
     * Delete a team (must be deletable and have no users)
     * @param id Team ID to delete
     * @returns Observable of deletion result
     */
    deleteTeam(id: string): Observable<any> {
        this.logger.i('Deleting team:', id);
        return this.http.delete(`${this.baseUrl}/v1/teams/${id}`);
    }
}
